import { store } from "@base/store";
import { LoaderSnackbarWrapperActions } from "@components/organisms/LoaderSnackbarWrapper/LoaderSnackbarWrapper.slice";
import { Response, httpGet, httpPost } from "@config/http";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { trackPromise } from "react-promise-tracker";

import {
  GetTransactionsPayload,
  getPaymentsIntentsPayload,
} from "./transaction.type";

const { REACT_APP_BASE_URL } = process.env;

const initialState = {};

const userHasStripeAccount = createAsyncThunk(
  "stripe/Fetches if there is an existing stripe account",
  async () => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpGet(`${REACT_APP_BASE_URL}/api/v1/stripe/account/`, true)
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const getBalance = createAsyncThunk("stripe/get Balance", async () => {
  let results: Response | null;
  try {
    results = await trackPromise(
      httpGet(`${REACT_APP_BASE_URL}/api/v1/stripe/account/balance`, true)
    );
    return Promise.resolve(results?.data);
  } catch (error) {
    return Promise.reject(error);
  }
});

const getPaymentsIntentsByGameId = createAsyncThunk(
  "stripe/Get all payment intents",
  async (id?: string) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpGet(
          `${REACT_APP_BASE_URL}/api/v1/stripe/payment/intents?game_id=${id}`,
          true
        )
      );
      return Promise.resolve(results?.data?.items);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const getPaymentsIntents = createAsyncThunk(
  "stripe/Get all payment intents",
  async (payload: getPaymentsIntentsPayload) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpGet(
          `${REACT_APP_BASE_URL}/api/v1/stripe/payment/intents?limit=${payload?.limit}&offset=${payload?.offset}`,
          true
        )
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const getPaymentsIntentsGameId = createAsyncThunk(
  "stripe/Get all payment intents",
  async (payload: getPaymentsIntentsPayload) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpGet(
          `${REACT_APP_BASE_URL}/api/v1/stripe/payment/intents?game_id=${payload.gameId}&limit=${payload.limit}&offset=${payload.offset}`,
          true
        )
      );
      return Promise.resolve(results?.data?.items);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const createPaymentIntent = createAsyncThunk(
  "stripe/Creates a payment intent on stripe and returns the client secret",
  async (body: { game_id: string }) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpPost(
          `${REACT_APP_BASE_URL}/api/v1/stripe/payment/intent`,
          body,
          true
        )
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const getTransactionsByGameId = createAsyncThunk(
  "stripe/Get all connected account transactions",
  async (payload: GetTransactionsPayload) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpGet(
          `${REACT_APP_BASE_URL}/api/v1/stripe/payment/transactions?game_id=${payload.gameId}&limit=${payload.limit}&offset=${payload.offset}`,
          true
        )
      );
      return Promise.resolve(results?.data?.items);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const getTransactions = createAsyncThunk(
  "stripe/Get all connected account transactions",
  async (payload: GetTransactionsPayload) => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpGet(
          `${REACT_APP_BASE_URL}/api/v1/stripe/payment/transactions?limit=${payload.limit}&offset=${payload.offset}`,
          true
        )
      );
      return Promise.resolve(results?.data?.items);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const fetchAccountDashboardLink = createAsyncThunk(
  "stripe/fetch account dashboard link",
  async () => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpGet(`${REACT_APP_BASE_URL}/api/v1/stripe/account/dashboard`, true)
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const fetchAccountOnboardingLink = createAsyncThunk(
  "stripe/fetch account onboarding link",
  async () => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpPost(`${REACT_APP_BASE_URL}/api/v1/stripe/account/link`, {}, true)
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const createAccount = createAsyncThunk(
  "stripe/Creates an account on stripe and returns the account id",
  async () => {
    let results: Response | null;
    try {
      results = await trackPromise(
        httpPost(`${REACT_APP_BASE_URL}/api/v1/stripe/account/`, {}, true)
      );
      return Promise.resolve(results?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
);

const createPayout = createAsyncThunk("stripe/Creates payout", async () => {
  let results: Response | null;
  try {
    results = await trackPromise(
      httpPost(`${REACT_APP_BASE_URL}/api/v1/stripe/account/payout`, {}, true)
    );
    store.dispatch(
      LoaderSnackbarWrapperActions.setGlobalAppearances({
        alert: {
          isVisible: true,
          message: results?.data?.message,
          type: "success",
        },
      })
    );
    return Promise.resolve(results?.data);
  } catch (error) {
    return Promise.reject(error);
  }
});

const transactionsSlice = createSlice({
  name: "transactions",
  initialState: initialState,
  reducers: {
    reset: () => initialState,
  },
  extraReducers: (builder) => {},
});

export const transactionsReducer = transactionsSlice.reducer;

export const transactionsActions = {
  ...transactionsSlice.actions,
  userHasStripeAccount,
  getBalance,
  getPaymentsIntents,
  createPaymentIntent,
  getPaymentsIntentsByGameId,
  getTransactionsByGameId,
  getTransactions,
  getPaymentsIntentsGameId,
  fetchAccountDashboardLink,
  fetchAccountOnboardingLink,
  createAccount,
  createPayout,
};
